import React, { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const STORAGE_KEY = "widget_v2_upfront_rules_collapsed";

// A browser told to block site data throws on the property itself, so both ends
// are guarded. Failing to remember the state only costs a client the panel
// reopening next visit — never the rules themselves.
function readCollapsed() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed) {
  try {
    if (collapsed) localStorage.setItem(STORAGE_KEY, "1");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the panel simply starts open again next time.
  }
}

/**
 * The rules that govern how a pack is bought. Open on arrival so a first-time
 * buyer cannot miss them, and collapsible because a returning one has read them
 * and is here to pick dates. The heading stays put either way, so collapsing
 * files the rules away rather than losing them.
 */
function PickerRulesPanel({ title, rules, sx }) {
  const [open, setOpen] = useState(() => !readCollapsed());

  const toggle = () => {
    setOpen((prev) => {
      writeCollapsed(prev);
      return !prev;
    });
  };

  if (!rules.length) return null;

  return (
    // Alert's message ships with overflow:auto. Mid-collapse the content is briefly
    // taller than its box, so that renders a vertical scrollbar, which steals width
    // and brings a horizontal one with it. Collapse does the clipping already.
    <Alert
      icon={false}
      severity="info"
      sx={{
        mb: 2,
        px: { xs: 1.5, sm: 2 },
        "& .MuiAlert-message": { flex: 1, minWidth: 0, overflow: "visible", py: { xs: 0.75, sm: 1 } },
        ...sx
      }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, cursor: "pointer", userSelect: "none" }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          {title}
        </Typography>
        <ExpandMoreIcon fontSize="small" sx={{ flexShrink: 0, transition: "transform 150ms", transform: open ? "rotate(180deg)" : "none" }} />
      </Box>

      <Collapse in={open}>
        {rules.length === 1 ? (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {rules[0]}
          </Typography>
        ) : (
          <Box
            component="ul"
            sx={{
              m: 0,
              mt: 0.5,
              pl: { xs: 2.25, sm: 2.5 },
              listStyleType: "disc",
              "& > li": { display: "list-item" },
              "& > li + li": { mt: 0.5 }
            }}
          >
            {rules.map((rule, i) => (
              <Typography key={i} component="li" variant="body2">
                {rule}
              </Typography>
            ))}
          </Box>
        )}
      </Collapse>
    </Alert>
  );
}

export default PickerRulesPanel;
